import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, RefreshCcw, Building2 } from 'lucide-react';
import api from '@/api/api';
import ReactMarkdown from 'react-markdown';

const TenantPolicyPage = () => {
  const { tenant, type } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPolicy();
  }, [tenant, type]);

  const fetchPolicy = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/policies/public/tenant/${tenant}/${type}`);
      setPolicy(response.data);
    } catch (err) {
      setError('Policy not found');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'privacy':
        return <Shield className="w-8 h-8" />;
      case 'terms':
        return <FileText className="w-8 h-8" />;
      case 'refund':
        return <RefreshCcw className="w-8 h-8" />;
      default:
        return <FileText className="w-8 h-8" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Policy Not Found</h1>
          <Link to={`/${tenant}/login`} className="text-blue-600 hover:underline">Go back to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to={`/${tenant}/login`} className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
              {getIcon()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{policy?.title}</h1>
              {policy?.tenant_name && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {policy.tenant_name}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Default Policy Notice */}
      {policy?.is_default && (
        <div className="max-w-4xl mx-auto px-4 pt-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm">
            This is a default policy template. The institution may update this with their specific policies.
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="prose prose-blue max-w-none">
            <ReactMarkdown>{policy?.content}</ReactMarkdown>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <Link to={`/${tenant}/policy/privacy`} className="hover:text-blue-600">Privacy Policy</Link>
            <Link to={`/${tenant}/policy/terms`} className="hover:text-blue-600">Terms of Use</Link>
            <Link to={`/${tenant}/policy/refund`} className="hover:text-blue-600">Refund Policy</Link>
          </div>
          {policy?.tenant_name && (
            <p className="text-center text-gray-500 text-sm mt-4">
              © 2026 {policy.tenant_name}. All rights reserved.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};

export default TenantPolicyPage;
